<template>
  <div>
    <Head :title="`${form.first_name} ${form.last_name}`" />
    <div class="flex justify-start mb-8 max-w-3xl">
      <h1 class="mb-8 text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" href="/staffs/nafa-staffs">
          {{ $t('message.staffs.staffs', 2) }}
        </Link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ form.first_name }} {{ form.last_name }}
      </h1>
      <img v-if="staff.photo" class="block ml-4 w-8 h-8 rounded-full" :src="staff.photo" />
    </div>
    <trashed-message v-if="staff.deleted_at" class="mb-6" @restore="restore"> This staff has been deleted. </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.first_name" :disabled="!canEdit()" :error="form.errors.first_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.first_name')" />
          <text-input v-model="form.last_name" :disabled="!canEdit()" :error="form.errors.last_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.last_name')" />
          <text-input v-model="form.email" :disabled="!canEdit()" pattern=".+@nafafs\.com" :title="$t('message.login.email_title')" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.email')" />
          <text-input v-model="form.password" :error="form.errors.password" class="pb-8 pr-6 w-full lg:w-1/2" type="password" autocomplete="new-password" :label="$t('message.staffs.password')" />
          <select-input v-model="form.account_id" :disabled="!canEdit()" :error="form.errors.account_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.country')">
            <option :value="null" />
            <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.country }} - {{ country.flag }}</option>
          </select-input>
          <select-input v-model="form.department_id" :disabled="!canEdit()" :error="form.errors.department_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.department')">
            <option :value="null" />
            <option v-for="department in departments" :key="department.id" :value="department.id">{{ $t(`message.departments.${slugifyDepartment(department.name)}`) }} - {{ department.account.country }} {{ department.account.flag }}</option>
          </select-input>
          <select-input v-model="form.user_type_id" :disabled="!canEdit()" :error="form.errors.user_type_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.role')">
            <option :value="null" />
            <option v-for="user_type in user_types" :key="user_type.id" :value="user_type.id">
              {{ $t(`message.staffs.${slugifyRole(user_type.name)}`) }}
            </option>
          </select-input>
          <select-input v-model="form.gender" :disabled="!canEdit()" :error="form.errors.gender" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.general.gender')">
            <option :value="null" />
            <option v-for="gender in genders" :key="gender.id" :value="gender.id">
              {{ $t(`message.general.${slugifyGender(gender.name)}`) }}
            </option>
          </select-input>
          <text-input v-model="form.phone" :error="form.errors.phone" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.phone')" />
          <text-input v-model="form.nafa_id" :disabled="!canEdit()" :error="form.errors.nafa_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.nafa_id')" />
          <text-input v-model="form.designation" :disabled="!canEdit()" :error="form.errors.designation" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.designation')" />
          <date-picker v-model="form.date_joined" :disabled="!canEdit()" :error="form.errors.date_joined" class="pb-8 pr-6 w-full" :label="$t('message.staffs.date_employed')" />
          <text-input v-model="form.id_number" :error="form.errors.id_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.id_number')" />
          <text-input v-model="form.social_security_number" :error="form.errors.social_security_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.social_security_number')" />
          <text-input v-model="form.tin_number" :error="form.errors.tin_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.tin_number')" />
          <date-picker v-model="form.date_of_birth" :error="form.errors.date_of_birth" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.date_of_birth')" />
          <text-input v-model="form.nationality" :error="form.errors.nationality" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.nationality')" />
          <text-input v-model="form.residential_address" :error="form.errors.residential_address" class="pb-8 pr-6 w-full" :label="$t('message.staffs.residential_address')" />
          <select-input v-model="form.marital_status" :error="form.errors.marital_status" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.marital_status')">
            <option :value="null" />
            <option v-for="status in maritalStatuses" :key="status.id" :value="status.id">
              {{ $t(`message.staffs.${status.name.toLowerCase()}`) }}
            </option>
          </select-input>
          <text-input v-model="form.emergency_contact_name" :error="form.errors.emergency_contact_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.emergency_contact_name')" />
          <text-input v-model="form.emergency_contact_number" :error="form.errors.emergency_contact_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.emergency_contact_number')" />
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!staff.deleted_at && canEdit()" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
            {{ $t('message.staffs.delete_staff') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('message.staffs.update_staff') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput.vue'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import DatePicker from '@/Shared/DatePicker'
import RoleChecker from '../../../Shared/Helpers/RoleChecker'

export default {
  components: {
    Head,
    Icon,
    Link,
    LoadingButton,
    TextInput,
    TextareaInput,
    SelectInput,
    TrashedMessage,
    DatePicker,
    RoleChecker,
  },
  layout: Layout,
  props: {
    staff: Object,
    user_types: Array,
    departments: Array,
    countries: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        account_id: this.staff.account_id,
        first_name: this.staff.first_name,
        last_name: this.staff.last_name,
        email: this.staff.email,
        password: '',
        phone: this.staff.phone,
        date_joined: this.staff.date_joined,
        nafa_id: this.staff.nafa_id,
        gender: this.staff.gender,
        designation: this.staff.designation,
        user_type_id: this.staff.user_type_id,
        department_id: this.staff.department_id,
        account_id: this.staff.account_id,
        id_number: this.staff.id_number,
        social_security_number: this.staff.social_security_number,
        tin_number: this.staff.tin_number,
        date_of_birth: this.staff.date_of_birth,
        nationality: this.staff.nationality,
        residential_address: this.staff.residential_address,
        marital_status: this.staff.marital_status,
        emergency_contact_name: this.staff.emergency_contact_name,
        emergency_contact_number: this.staff.emergency_contact_number,
      }),
      genders: [
        { id: 'male', name: 'Male' },
        { id: 'female', name: 'Female' },
      ],
      maritalStatuses: [
        { id: 'single', name: 'Single' },
        { id: 'married', name: 'Married' },
        { id: 'divorced', name: 'Divorced' },
        { id: 'separated', name: 'Separated' },
        { id: 'widowed', name: 'Widowed' },
      ],
    }
  },
  methods: {
    hasRole(role) {
      var userRole = new RoleChecker(this.$page.props)
      return userRole.hasRole(role)
    },
    canEdit(staff_account_id) {
      const user_account_id = this.$page.props.auth.user.account_id
      return this.hasRole(['hr', 'admin']) && user_account_id == staff_account_id
    },
    update() {
      this.form.put(`/staffs/nafa-staffs/${this.staff.id}`, {
        onSuccess: () => this.form.reset('password', 'photo'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this staff?')) {
        this.$inertia.delete(`/staffs/nafa-staffs/${this.staff.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this staff?')) {
        this.$inertia.put(`/staffs/nafa-staffs/${this.staff.id}/restore`)
      }
    },
    slugifyRole(role) {
      return role.toLowerCase().replace(' ', '_').replace('-', '_')
    },
    slugifyDepartment(department) {
      if (department.charAt(department.length - 1) == '.') {
        department = department.slice(0, -1)
      }
      return department.toLowerCase().replace(/\./g, '').replace(/&/g, 'and').replace(/\s+/g, '_')
    },
    slugifyGender(gender) {
      return gender.toLowerCase()
    },
  },
}
</script>
